import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { BiLinkExternal } from 'react-icons/bi';
import { BsGithub, BsArrowLeft, BsArrowRight } from 'react-icons/bs';
import { AppWrap } from '../../wrapper/index';
import { urlFor, client } from '../../client';
import './Work.scss';

const fadeUp = {
	hidden: { y: 40, opacity: 0 },
	visible: { y: 0, opacity: 1, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
};

const cardReveal = {
	hidden: { x: 70, opacity: 0 },
	visible: (i) => ({
		x: 0,
		opacity: 1,
		transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: (i % 4) * 0.08 },
	}),
};

const Work = () => {
	const [activeFilter, setActiveFilter] = useState('All');
	const [works, setWorks] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [canPrev, setCanPrev] = useState(false);
	const [canNext, setCanNext] = useState(false);
	const trackRef = useRef(null);
	const progressBarRef = useRef(null);
	const progressThumbRef = useRef(null);

	useEffect(() => {
		let mounted = true;
		client
			.fetch('*[_type == "works"]')
			.then((data) => {
				if (mounted) {
					setWorks(data || []);
					setIsLoading(false);
				}
			})
			.catch(() => {
				if (mounted) setIsLoading(false);
			});
		return () => {
			mounted = false;
		};
	}, []);

	const filters = useMemo(() => {
		const tags = works
			.flatMap((work) => work.tags || [])
			.filter((tag) => tag && tag.trim().toLowerCase() !== 'all');
		return ['All', ...new Set(tags)];
	}, [works]);

	const filterWorks = useMemo(
		() =>
			activeFilter === 'All'
				? works
				: works.filter((work) => (work.tags || []).includes(activeFilter)),
		[works, activeFilter]
	);

	const updateSlider = useCallback(() => {
		const el = trackRef.current;
		if (!el) return;
		const maxScroll = el.scrollWidth - el.clientWidth;
		setCanPrev(el.scrollLeft > 4);
		setCanNext(el.scrollLeft < maxScroll - 4);

		const bar = progressBarRef.current;
		const thumb = progressThumbRef.current;
		if (bar && thumb) {
			const ratio = el.scrollWidth > 0 ? el.clientWidth / el.scrollWidth : 1;
			const progress = maxScroll > 0 ? el.scrollLeft / maxScroll : 0;
			thumb.style.width = `${Math.min(ratio, 1) * 100}%`;
			thumb.style.transform = `translateX(${progress * bar.clientWidth * (1 - ratio)}px)`;
		}
	}, []);

	useEffect(() => {
		const el = trackRef.current;
		if (!el) return;
		updateSlider();
		el.addEventListener('scroll', updateSlider, { passive: true });
		window.addEventListener('resize', updateSlider);
		return () => {
			el.removeEventListener('scroll', updateSlider);
			window.removeEventListener('resize', updateSlider);
		};
	}, [updateSlider, activeFilter, isLoading, filterWorks.length]);

	const scrollByCard = (direction) => {
		const el = trackRef.current;
		if (!el) return;
		const card = el.querySelector('.work-card');
		const step = card ? card.getBoundingClientRect().width + 20 : el.clientWidth * 0.8;
		el.scrollBy({ left: direction * step, behavior: 'smooth' });
	};

	return (
		<div className='work-content'>
			<motion.div
				className='work-head'
				variants={fadeUp}
				initial='hidden'
				whileInView='visible'
				viewport={{ once: true, amount: 0.4 }}
			>
				<div>
					<p className='eyebrow'>Selected Work</p>
					<h2 className='work-heading'>
						Featured <span className='text-gradient'>projects</span>
						<br />
						<em>built to convert</em>
					</h2>
				</div>
				<p className='work-intro'>
					Client builds and personal experiments — from e-commerce storefronts to
					animated product sites. Filter by stack, then slide through.
				</p>
			</motion.div>

			{filters.length > 1 && (
				<motion.div
					className='work-filter'
					role='group'
					aria-label='Filter projects by technology'
					variants={fadeUp}
					initial='hidden'
					whileInView='visible'
					viewport={{ once: true, amount: 0.4 }}
				>
					{filters.map((item) => (
						<button
							key={item}
							type='button'
							onClick={() => setActiveFilter(item)}
							aria-pressed={activeFilter === item}
							className={`work-filter-chip ${activeFilter === item ? 'active' : ''}`}
						>
							{item}
						</button>
					))}
				</motion.div>
			)}

			{isLoading ? (
				<div className='work-track is-static' aria-hidden='true'>
					{[0, 1, 2].map((i) => (
						<div className='work-card-skeleton' key={i}>
							<div className='skeleton-media' />
							<div className='skeleton-line w-40' />
							<div className='skeleton-line w-80' />
							<div className='skeleton-line w-60' />
						</div>
					))}
				</div>
			) : filterWorks.length > 0 ? (
				<>
					<div
						key={activeFilter}
						ref={trackRef}
						className='work-track'
						aria-label='Projects slider'
					>
						{filterWorks.map((work, index) => (
							<motion.article
								className='work-card'
								custom={index}
								variants={cardReveal}
								initial='hidden'
								whileInView='visible'
								viewport={{ once: true, amount: 0.35 }}
								key={`${work.title}-${index}`}
							>
								<a
									className='work-card-media'
									href={work.projectLink}
									target='_blank'
									rel='noreferrer'
									aria-label={`Open ${work.title} live project`}
								>
									<img src={urlFor(work.imgUrl)} alt={work.title} loading='lazy' />
									<span className='work-card-num' aria-hidden='true'>
										{String(index + 1).padStart(2, '0')}
									</span>
								</a>

								<div className='work-card-body'>
									{work.tags && work.tags.length > 0 && (
										<div className='work-card-tags'>
											{work.tags
												.filter((tag) => tag.trim().toLowerCase() !== 'all')
												.slice(0, 3)
												.map((tag) => (
													<span key={tag}>{tag}</span>
												))}
										</div>
									)}
									<h3 className='work-card-title'>
										<a href={work.projectLink} target='_blank' rel='noreferrer'>
											{work.title}
										</a>
									</h3>
									<p className='work-card-desc'>{work.description}</p>

									<div className='work-card-links'>
										{work.projectLink && (
											<a
												href={work.projectLink}
												target='_blank'
												rel='noreferrer'
												className='work-link primary'
											>
												<BiLinkExternal aria-hidden='true' />
												<span>Live Site</span>
											</a>
										)}
										{work.codeLink && (
											<a
												href={work.codeLink}
												target='_blank'
												rel='noreferrer'
												className='work-link'
											>
												<BsGithub aria-hidden='true' />
												<span>Code</span>
											</a>
										)}
									</div>
								</div>
							</motion.article>
						))}
					</div>

					<div className='work-slider-controls'>
						<div className='slider-progress' ref={progressBarRef} aria-hidden='true'>
							<div className='slider-progress-thumb' ref={progressThumbRef} />
						</div>
						<div className='slider-arrows'>
							<button
								type='button'
								className='slider-btn'
								onClick={() => scrollByCard(-1)}
								disabled={!canPrev}
								aria-label='Previous projects'
							>
								<BsArrowLeft />
							</button>
							<button
								type='button'
								className='slider-btn'
								onClick={() => scrollByCard(1)}
								disabled={!canNext}
								aria-label='Next projects'
							>
								<BsArrowRight />
							</button>
						</div>
					</div>
				</>
			) : (
				<div className='work-empty'>
					<p>
						{works.length === 0
							? 'Projects are on their way — check back soon.'
							: `Nothing under “${activeFilter}” yet.`}
					</p>
					{works.length > 0 && (
						<button
							type='button'
							className='btn btn-ghost'
							onClick={() => setActiveFilter('All')}
						>
							Show all work
						</button>
					)}
				</div>
			)}
		</div>
	);
};

export default AppWrap(Work, 'work');
